// Helper murni notifikasi admin (tabel admin_notifications, mig 078).
// Sumber event = trigger DB (lihat migrasi); UI bell di AdminHeader hanya
// membaca baris + menandai is_read. Semua fungsi di sini pure & teruji.

export const ADMIN_NOTIFICATION_TYPES = [
  "invoice_requested",
  "invoice_payment_submitted",
  "invoice_paid_balance",
  "shipment_requested",
  "shipment_goods_complete",
  "shipment_waiting_goods",
  "topup_requested",
  "customer_registered",
  "reseller_application",
  "customer_level_changed",
  "order_created",
  "order_created_ready",
  "po_closed_empty",
  "invoice_auto_voided",
  "backup_stale",
  "autoship_left_behind",
  "feedback_submitted",
  "ai_drafts_created",
  "invoice_cancelled_by_buyer",
  // Integrasi Shopee S7 (migrasi notifikasi_shopee + shopee_rekonsiliasi_pantauan).
  "shopee_connection_problem",
  "shopee_stock_push_failed",
  "shopee_reconcile_mismatch",
  "shopee_order_negative_stock",
  // Auto-Posting Instagram AP0 (migrasi notifikasi_instagram_foto_terputus).
  "instagram_photos_interrupted",
] as const;

export type AdminNotificationType = (typeof ADMIN_NOTIFICATION_TYPES)[number];

export type AdminNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  /** Konteks pelanggan (null = notif sistem, mis. po_closed_empty). */
  customerId: string | null;
  isRead: boolean;
  createdAt: string;
};

/** Baris mentah dari Supabase (juga payload Realtime). */
export type AdminNotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  customer_id?: string | null;
  is_read: boolean;
  created_at: string;
};

export function mapAdminNotificationRow(
  row: AdminNotificationRow
): AdminNotification {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    body: row.body,
    link: row.link ?? null,
    customerId: row.customer_id ?? null,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

/**
 * Filter "Reseller Saya" halaman notifikasi admin (cermin toggle Orderan Saya /
 * Semua Orderan di Tagihan). `scopedCustomerIds` null = mode "Semua" (atau
 * scope gagal dimuat) → semua baris lolos. Saat scope aktif, notif lolos bila
 * konteks pelanggannya termasuk reseller yang di-handle admin ini; notif TANPA
 * konteks pelanggan (`customerId` null, mis. po_closed_empty) TETAP lolos
 * karena merupakan peristiwa sistem — bukan aktivitas reseller admin lain.
 */
export function notifInScope(
  customerId: string | null,
  scopedCustomerIds: string[] | null
): boolean {
  if (scopedCustomerIds === null) return true;
  if (customerId === null) return true;
  return scopedCustomerIds.includes(customerId);
}

/**
 * Teks konfirmasi tombol "Hapus Semua" di halaman notifikasi admin.
 *
 * Diturunkan dari NILAI YANG SAMA yang dikirim ke RPC
 * `clear_my_admin_notifications` (`scopedCustomerIds`) — BUKAN dari mode
 * toggle. Bedanya penting: mode "Reseller Saya" yang daftar resellernya gagal
 * dimuat jatuh ke `null`, dan saat itu daftar menampilkan SEMUA notifikasi,
 * jadi penghapusan pun menyapu semua. Kalimat yang mengikuti `mode` akan
 * menjanjikan pembatasan yang tidak terjadi; kalimat yang mengikuti argumen
 * RPC selalu jujur.
 */
export function adminNotifClearConfirmText(
  scopedCustomerIds: string[] | null
): string {
  if (scopedCustomerIds === null) {
    return "Hapus SEMUA notifikasi Anda? Tindakan ini tidak dapat dibatalkan.";
  }
  return (
    `Hapus notifikasi dari ${scopedCustomerIds.length} reseller yang Anda ` +
    "handle (termasuk notifikasi sistem)? Notifikasi reseller admin lain TIDAK " +
    "ikut terhapus. Tindakan ini tidak dapat dibatalkan."
  );
}

export function adminNotifIcon(type: string): string {
  switch (type) {
    case "invoice_requested":
      return "🧾";
    case "invoice_payment_submitted":
      return "🏦";
    case "invoice_paid_balance":
      return "💰";
    case "shipment_requested":
      return "🚚";
    case "shipment_goods_complete":
      return "✅";
    case "shipment_waiting_goods":
      return "⏳";
    case "topup_requested":
      return "💳";
    case "customer_registered":
      return "🆕";
    case "reseller_application":
      return "🤝";
    case "customer_level_changed":
      return "⭐";
    case "order_created":
      return "🛒";
    case "order_created_ready":
      return "📦";
    case "po_closed_empty":
      return "📭";
    case "invoice_auto_voided":
      return "🗓️";
    case "backup_stale":
      return "🗄️";
    case "autoship_left_behind":
      return "🚚";
    case "feedback_submitted":
      return "📣";
    case "ai_drafts_created":
      return "🤖";
    case "invoice_cancelled_by_buyer":
      return "↩️";
    case "shopee_connection_problem":
      return "⛔";
    case "shopee_stock_push_failed":
      return "📤";
    case "shopee_reconcile_mismatch":
      return "🔁";
    case "shopee_order_negative_stock":
      return "➖";
    case "instagram_photos_interrupted":
      return "📸";
    // Tagihan langganan (mig 8170/8190/8310) — sengaja di luar ADMIN_NOTIFICATION_TYPES:
    // kabar langganan tak bisa dimatikan dari Pengaturan Notifikasi.
    case "subscription_invoice_approved":
      return "📅";
    case "subscription_invoice_rejected":
      return "⚠️";
    case "subscription_invoice_issued":
      return "🧾";
    default:
      return "🔔";
  }
}

/** Label Indonesia per jenis notifikasi admin (dipakai daftar Pengaturan). */
export function adminNotifTypeLabel(type: string): string {
  switch (type) {
    case "invoice_requested":
      return "Pengajuan tagihan";
    case "invoice_payment_submitted":
      return "Bukti transfer tagihan masuk";
    case "invoice_paid_balance":
      return "Tagihan dibayar via saldo";
    case "shipment_requested":
      return "Paket baru: request kirim & Tagihan + Kirim";
    case "shipment_goods_complete":
      return "Paket menunggu barang kini lengkap";
    case "shipment_waiting_goods":
      return "Pengingat paket menunggu barang";
    case "topup_requested":
      return "Permintaan top-up saldo";
    case "customer_registered":
      return "Pendaftaran pelanggan baru";
    case "reseller_application":
      return "Pendaftaran mitra/reseller";
    case "customer_level_changed":
      return "Level reseller berubah";
    case "order_created":
      return "Pesanan pre-order baru (checkout)";
    case "order_created_ready":
      return "Pesanan ready stock baru (checkout)";
    case "po_closed_empty":
      return "PO ditutup tanpa pesanan";
    case "invoice_auto_voided":
      return "Pengajuan tagihan kedaluwarsa";
    case "backup_stale":
      return "Pengingat backup database";
    case "autoship_left_behind":
      return "Barang tertinggal dari kirim otomatis";
    case "feedback_submitted":
      return "Kritik & saran dari pembeli";
    case "ai_drafts_created":
      return "Draf produk dari agen AI";
    case "invoice_cancelled_by_buyer":
      return "Tagihan dibatalkan pembeli";
    case "shopee_connection_problem":
      return "Sinkron Shopee berhenti";
    case "shopee_stock_push_failed":
      return "Stok gagal dikirim ke Shopee";
    case "shopee_reconcile_mismatch":
      return "Stok Shopee beda dengan website";
    case "shopee_order_negative_stock":
      return "Pesanan Shopee membuat stok minus";
    case "instagram_photos_interrupted":
      return "Posting Instagram belum terkirim";
    default:
      return type;
  }
}

/** Contoh notifikasi (judul + isi) per jenis — cermin format trigger SQL. */
export function adminNotifExample(type: string): { title: string; body: string } {
  switch (type) {
    case "invoice_requested":
      return {
        title: "Pengajuan Tagihan",
        body: "Sari mengajukan Tagihan #1024 — menunggu ACC.",
      };
    case "invoice_payment_submitted":
      return {
        title: "Bukti Transfer Masuk",
        body: "Sari mengirim bukti transfer untuk Tagihan #1024 — perlu konfirmasi.",
      };
    case "invoice_paid_balance":
      return {
        title: "Pembayaran Saldo Diterima",
        body: "Sari membayar Rp150.000 via saldo untuk Tagihan #1024 — LUNAS.",
      };
    case "shipment_requested":
      // Bentuk terbanyak: paket dari Tagihan + Kirim yang lunas (migrasi
      // notifikasi_paket_niat_kirim).
      // Request Kirim pembeli tetap «Sari mengajukan request kirim — atur ongkir di board.»
      return {
        title: "Paket Siap Dikemas",
        body: "Tagihan #1024 Sari sudah lunas. Paketnya masuk Dikemas.",
      };
    case "shipment_goods_complete":
      return {
        title: "Paket Siap Dikemas",
        body: "Semua barang paket KRM2610000123 (Sari) sudah ready — kartunya kini di Dikemas · Ready Lengkap.",
      };
    case "shipment_waiting_goods":
      return {
        title: "Paket Menunggu Barang",
        body: "Paket KRM2610000123 (Sari) sudah 8 hari menunggu 4 pcs barang Belum Ready — cek produksinya, atau keluarkan barangnya lewat Revisi Isi Paket.",
      };
    case "topup_requested":
      return {
        title: "Permintaan Top-up Saldo",
        body: "Sari mengajukan top-up Rp200.000 — perlu verifikasi.",
      };
    case "customer_registered":
      return {
        title: "Pendaftaran Pelanggan Baru",
        body: "Sari (Toko Sari) mendaftar (No. WA 081234567890) — akun langsung aktif sebagai pelanggan umum. Silakan follow-up & tawarkan jadi mitra.",
      };
    case "reseller_application":
      return {
        title: "Pengajuan Mitra Baru",
        body: "Sari mengajukan jadi Agen + top-up Rp 100.000 — approve top-upnya di Kelola Saldo, level naik otomatis.",
      };
    case "customer_level_changed":
      return {
        title: "Level Reseller Berubah",
        body: "Level Sari berubah: Reseller → Agen.",
      };
    case "order_created":
      return {
        title: "Pesanan PO Baru",
        body: "Sari memesan Gamis Syari (3 pcs).",
      };
    case "order_created_ready":
      return {
        title: "Pesanan Ready Stock Baru",
        body: "Sari memesan Kaos Polos (2 pcs) — masuk Keranjang Ready.",
      };
    case "po_closed_empty":
      return {
        title: "PO Ditutup Tanpa Pesanan",
        body: "Gamis Syari closed PO — tidak ada yang order.",
      };
    case "invoice_auto_voided":
      return {
        title: "Pengajuan Tagihan Kedaluwarsa",
        body: "Pengajuan tagihan #1024 dibatalkan otomatis karena tidak di-ACC sampai lewat tempo. Barang pembeli kembali ke antrean keep.",
      };
    case "autoship_left_behind":
      return {
        title: "Barang Tertinggal dari Kirim Otomatis",
        body: "Tagihan TGH-2609020033: 2 pcs sudah ditagih tapi TIDAK ikut paket yang sudah terbit, dan tidak akan menyusul sendiri. Masukkan lewat Revisi Isi Paket di halaman pengirimannya, atau kirim terpisah lewat Request Kirim.",
      };
    case "backup_stale":
      return {
        title: "Backup Database Sudah Lama",
        body: "Backup database terakhir 9 hari lalu — disarankan backup rutin (atau aktifkan backup otomatis).",
      };
    case "feedback_submitted":
      return {
        title: "Masukan Baru dari Pembeli",
        body: "Kiriman anonim · kritik — buka Kritik & Saran untuk membacanya.",
      };
    case "ai_drafts_created":
      return {
        title: "3 draf produk dari AI",
        body: "OpenClaw Galea membuat draf — terakhir: Gamis Aisyah. Periksa lalu aktifkan.",
      };
    case "invoice_cancelled_by_buyer":
      return {
        title: "Tagihan Dibatalkan Pembeli",
        body: "Sari membatalkan tagihan Barang #1024. Barangnya kembali ke keranjang pembeli.",
      };
    // Integrasi Shopee S7 — cermin teks pemicu migrasi shopee_rekonsiliasi_pantauan.
    case "shopee_connection_problem":
      return {
        title: "Sinkron Shopee berhenti",
        body: "Galea Official: Shopee menolak kunci aplikasi, jadi stok dan pesanan tidak disinkronkan. Perbaiki di Hubungkan Shopee.",
      };
    case "shopee_stock_push_failed":
      return {
        title: "Stok gagal dikirim ke Shopee",
        body: "Ada stok produk Galea Official yang gagal dikirim ke Shopee. Cek dan kirim ulang di Hubungkan Shopee.",
      };
    case "shopee_reconcile_mismatch":
      return {
        title: "Stok Shopee beda dengan website",
        body: "3 produk di Galea Official beda stok dan sudah dikirim ulang. 1 di antaranya diubah langsung di Seller Centre.",
      };
    case "shopee_order_negative_stock":
      return {
        title: "Stok minus dari pesanan Shopee",
        body: "Pesanan Shopee 2611023ABCDEF membuat stok Gamis Aisyah jadi -1. Cek stoknya lewat Stok Opname.",
      };
    // Auto-Posting Instagram AP0 — cermin teks penyapu migrasi autopost_foto_menyusul.
    case "instagram_photos_interrupted":
      return {
        title: "Posting Instagram belum terkirim",
        body: "Foto Gamis Aisyah belum selesai disiapkan untuk Instagram. Buka produknya, lalu ketuk Coba lagi.",
      };
    default:
      return { title: "Notifikasi", body: "Contoh isi notifikasi." };
  }
}

/** Tujuan klik bila baris notif tidak membawa kolom `link`. */
export function adminNotifFallbackRoute(type: string): string {
  switch (type) {
    case "invoice_requested":
    case "invoice_payment_submitted":
    case "invoice_paid_balance":
    case "invoice_auto_voided":
    case "invoice_cancelled_by_buyer":
    case "autoship_left_behind":
      return "/admin/invoices";
    case "topup_requested":
      return "/admin/saldo";
    case "backup_stale":
      return "/admin/backup";
    case "customer_registered":
    case "customer_level_changed":
      return "/admin/customers";
    case "reseller_application":
      return "/admin/reseller-groups";
    case "feedback_submitted":
      return "/admin/kritik-saran";
    case "ai_drafts_created":
      return "/admin/settings/integrasi-ai";
    case "subscription_invoice_approved":
    case "subscription_invoice_rejected":
    case "subscription_invoice_issued":
      return "/admin/langganan";
    case "shopee_connection_problem":
    case "shopee_stock_push_failed":
    case "shopee_reconcile_mismatch":
    case "shopee_order_negative_stock":
      return "/admin/settings/sosial-marketplace/shopee-api";
    case "instagram_photos_interrupted":
      return "/admin/products";
    case "shipment_requested":
    case "shipment_goods_complete":
    case "shipment_waiting_goods":
    case "order_created":
    case "order_created_ready":
    case "po_closed_empty":
    default:
      return "/admin/orders";
  }
}

/** Umur notif gaya NotificationsClient ("Baru saja", "5 menit lalu", …). */
export function formatNotifAge(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Baru saja";
  if (diffMin < 60) return `${diffMin} menit lalu`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} jam lalu`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay} hari lalu`;
  return d.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: diffDay > 365 ? "numeric" : undefined,
  });
}

/** Label badge unread (cap "99+" seperti bell pembeli). */
export function formatUnreadBadge(count: number): string {
  return count > 99 ? "99+" : String(count);
}

// ─── Pengaturan Notifikasi (tabel admin_notification_settings, mig 134) ───────
// Setelan on/off per-jenis untuk bell admin. Default NYALA: jenis yang belum
// punya baris di DB tetap aktif (cermin gerbang insert_admin_notification).

export type AdminNotifSetting = {
  type: AdminNotificationType;
  enabled: boolean;
};

/** Baris mentah `admin_notification_settings` (type, enabled). */
export type AdminNotifSettingRow = {
  type: string;
  enabled: boolean | null;
};

/**
 * Petakan baris DB → daftar setelan untuk SEMUA jenis (lengkap walau DB belum
 * punya barisnya), urut sesuai ADMIN_NOTIFICATION_TYPES. Jenis yang tak ada di
 * DB default NYALA; baris tak dikenal diabaikan.
 */
export function mapAdminNotifSettingRows(
  rows: AdminNotifSettingRow[]
): AdminNotifSetting[] {
  const map = new Map(rows.map((r) => [r.type, r]));
  return ADMIN_NOTIFICATION_TYPES.map((type) => {
    const row = map.get(type);
    // Default nyala: hanya enabled=false eksplisit yang mematikan.
    return { type, enabled: row ? row.enabled !== false : true };
  });
}
